import React from "react";
import { useTranslation } from "react-i18next";
import {
  getClientId,
  translateGender,
  translateEthnicity,
  translateLanguage,
  translatePronouns,
  translateDiagnosis,
} from "../utils";
import { getIntlLocale } from "utils/getIntlLocale";
import { CustomerLabel } from "../Common/CustomerLabel";
import { HeaderMainTitle } from "../Common/HeaderMainTitle";
import {
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";

import { COUNTRIES, STATES, TIMEZONES } from "@constants";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { useCustomerDetails } from "hooks/useCustomerDetails";
import { HeaderSubtitle } from "../Common/HeaderSubtitle";
import { UserCircle } from "@phosphor-icons/react/dist/ssr";

interface DrawerClientProfileContentProps {
  onBack: () => void;
  user: any;
  customerDetails?: any;
}

export const DrawerClientProfileContent: React.FC<DrawerClientProfileContentProps> = ({
  onBack,
  user,
  customerDetails: customerDetailsProp,
}) => {
  const { t } = useTranslation(["clientHeader"]);
  const clientId = getClientId(user);
  const { data: customerDetailsFetched, isLoading, error } = useCustomerDetails(
    customerDetailsProp ? null : clientId
  );

  const customerDetails = customerDetailsProp || customerDetailsFetched;
  const title = t("clientHeader:clientProfile.titleWithName", {
    name: customerDetails?.name || t("clientHeader:menu.client"),
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error || !customerDetails) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("clientHeader:clientProfile.unableToLoad")}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ padding: "24px 24px 0px 24px" }}>
          <CustomerLabel label={t("clientHeader:clientProfile.firstName")} value={customerDetails?.name || "-"} />
          <CustomerLabel
            label={t("clientHeader:clientProfile.preferredFirstName")}
            value={customerDetails?.preferred_first_name || "-"}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.lastName")}
            value={customerDetails?.child_last_name || "-"}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.dateOfBirth")}
            value={
              customerDetails?.dob
                ? new Date(dateWithoutTimezone(customerDetails.dob)).toLocaleDateString(
                    getIntlLocale()
                  )
                : "-"
            }
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.sexAssignedAtBirth")}
            value={translateGender(t, customerDetails?.gender)}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.identifiedGender")}
            value={translateGender(t, customerDetails?.identified_gender)}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.transgender")}
            value={customerDetails?.transgender ? t("clientHeader:profile.yes") : t("clientHeader:profile.no")}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.pronouns")}
            value={translatePronouns(t, customerDetails?.pronouns)}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.diagnosis")}
            value={translateDiagnosis(t, customerDetails?.diagnosis)}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.clientLivesWith")}
            value={customerDetails?.lives_with || "-"}
          />

          <CustomerLabel
            label={t("clientHeader:clientProfile.raceEthnicity")}
            value={translateEthnicity(t, customerDetails?.ethnicity)}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.primaryLanguage")}
            value={translateLanguage(t, customerDetails?.primary_language)}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.interpreterRequired")}
            value={customerDetails?.needs_interpreter ? t("clientHeader:profile.yes") : t("clientHeader:profile.no")}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.preAuthRequired")}
            value={customerDetails?.pre_auth_required ? t("clientHeader:profile.yes") : t("clientHeader:profile.no")}
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.requiresTablet")}
            value={customerDetails?.requires_tablet ? t("clientHeader:profile.yes") : t("clientHeader:profile.no")}
          />
        </Box>

        <HeaderSubtitle>{t("clientHeader:clientProfile.location")}</HeaderSubtitle>
        <Box sx={{ padding: "0px 24px" }}>
          <CustomerLabel label={t("clientHeader:clientProfile.address")} value={customerDetails?.line1 || "-"} />
          <CustomerLabel label={t("clientHeader:clientProfile.address2")} value={customerDetails?.line2 || "-"} />
          <CustomerLabel
            label={t("clientHeader:clientProfile.state")}
            value={STATES[customerDetails?.state as keyof typeof STATES] || "-"}
          />
          <CustomerLabel label={t("clientHeader:clientProfile.city")} value={customerDetails?.city || "-"} />
          <CustomerLabel label={t("clientHeader:clientProfile.zipCode")} value={customerDetails?.zip || "-"} />
          <CustomerLabel
            label={t("clientHeader:clientProfile.timeZone")}
            value={
              TIMEZONES[customerDetails?.timezone_jhi as keyof typeof TIMEZONES] ||
              TIMEZONES[customerDetails?.timezone as keyof typeof TIMEZONES] ||
              "-"
            }
          />
          <CustomerLabel
            label={t("clientHeader:clientProfile.country")}
            value={COUNTRIES[customerDetails?.country as keyof typeof COUNTRIES] || "-"}
          />
        </Box>
      </>
    );
  };

  return (
    <>
      <HeaderMainTitle
        user={user}
        title={title}
        onBack={onBack}
        icon={UserCircle}
      />
      {renderContent()}
    </>
  );
};
